import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Terminal, Copy, Check, Eye, EyeOff, Loader2, ExternalLink, KeyRound,
  CloudUpload, ListOrdered, Rocket, ShieldCheck, FileJson,
} from 'lucide-react';
import { agentApiAPI } from '../lib/api';

const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const PREFIX = `${BASE}/api/agent/v1`;

const CURL_QUICKSTART = `BASE=${BASE}
KEY=<your-api-key>

# 1. create the listing (runs the normal AI pipeline)
LID=$(curl -s -X POST "$BASE/api/agent/v1/listings" \\
  -H "X-API-Key: $KEY" -H 'Content-Type: application/json' \\
  -d '{"input_type":"text",
       "input_text":"2.5ct oval lab grown diamond hidden halo ring, 14k yellow gold",
       "product_type":"engagement_ring",
       "request_id":"ring-0047-create"}' | jq -r .listing_id)

# 2. ask for an upload slot (MD5 is base64, not hex)
MD5=$(openssl dgst -md5 -binary hero.jpg | base64)
PRE=$(curl -s -X POST "$BASE/api/agent/v1/uploads/presign" \\
  -H "X-API-Key: $KEY" -H 'Content-Type: application/json' \\
  -d "{\\"filename\\":\\"hero.jpg\\",\\"content_type\\":\\"image/jpeg\\",
       \\"file_size\\":$(stat -c%s hero.jpg),\\"md5\\":\\"$MD5\\"}")

# 3. PUT the raw bytes with EXACTLY the headers we returned
curl -s -X PUT "$(echo "$PRE" | jq -r .upload_url)" \\
  -H "Content-Type: image/jpeg" -H "Content-MD5: $MD5" \\
  --data-binary @hero.jpg

# 4. attach it as the cover (order 0)
curl -s -X POST "$BASE/api/agent/v1/listings/$LID/media/attach" \\
  -H "X-API-Key: $KEY" -H 'Content-Type: application/json' \\
  -d "{\\"object_key\\":\\"$(echo "$PRE" | jq -r .object_key)\\",
       \\"order\\":0,\\"cover\\":true,\\"media_type\\":\\"photo\\",
       \\"request_id\\":\\"ring-0047-media-1\\"}"

# 5. wait for the AI copy
curl -s "$BASE/api/agent/v1/listings/$LID/wait?timeout=45" -H "X-API-Key: $KEY"`;

const PY_QUICKSTART = `import base64, hashlib, os, requests

BASE, KEY = "${BASE}", "<your-api-key>"
H = {"X-API-Key": KEY}

def upload(path, content_type):
    blob = open(path, "rb").read()
    md5 = base64.b64encode(hashlib.md5(blob).digest()).decode()
    pre = requests.post(f"{BASE}/api/agent/v1/uploads/presign", headers=H, json={
        "filename": os.path.basename(path),
        "content_type": content_type,
        "file_size": len(blob),
        "md5": md5,                       # storage verifies the body for you
    }).json()
    requests.put(pre["upload_url"], data=blob,
                 headers=pre["required_headers"]).raise_for_status()
    return pre["object_key"]              # 400 BadDigest = corrupted, re-upload

def attach(listing_id, object_key, order, cover=False, alt_text=None, request_id=None):
    r = requests.post(f"{BASE}/api/agent/v1/listings/{listing_id}/media/attach",
                      headers=H, json={"object_key": object_key, "order": order,
                                       "cover": cover, "alt_text": alt_text,
                                       "request_id": request_id})
    r.raise_for_status()
    return r.json()

listing = requests.post(f"{BASE}/api/agent/v1/listings", headers=H, json={
    "input_type": "text",
    "input_text": "2.5ct oval lab grown diamond hidden halo ring, 14k yellow gold",
    "product_type": "engagement_ring",
    "request_id": "ring-0047-create",
}).json()

for i, (path, ct) in enumerate([("hero.jpg", "image/jpeg"),
                                ("side.jpg", "image/jpeg"),
                                ("clip.mp4", "video/mp4")]):
    attach(listing["listing_id"], upload(path, ct), order=i, cover=(i == 0),
           request_id=f"ring-0047-media-{i}")`;

const AGENT_PROMPT = `You can control AJHQ through its Agent API.

Base URL: ${PREFIX}
Auth: send header \`X-API-Key: <your-api-key>\` on every request.
Full reference: GET /docs.md  ·  Machine spec: GET /openapi.json

To publish a listing with ordered media:
1. POST /listings with {"input_type":"text"|"url"|"image", the matching
   input_text/input_url/object_key, "product_type":"engagement_ring",
   "request_id":"<unique>"}. Keep the returned listing_id.
2. For EACH file, in the order it should appear:
   a. POST /uploads/presign with {filename, content_type, file_size, md5}
      where md5 is the base64 MD5 of the file.
   b. PUT the raw bytes to the returned upload_url using EXACTLY the headers in
      required_headers. A 400 BadDigest means the transfer corrupted — re-upload.
   c. POST /listings/{listing_id}/media/attach with
      {"object_key": <from presign>, "order": <0-based position>,
       "cover": true for the first photo, "media_type":"photo"|"video",
       "alt_text":"<short description>", "request_id":"<unique>"}
   Files over 2 GB are not accepted; presigned URLs expire after 1 hour — just
   presign again. For files under 10 MB you may instead POST multipart to
   /listings/{id}/media/upload.
3. GET /listings/{listing_id}/wait?timeout=45 in a loop while the response has
   poll_again=true (no request may exceed 60s), then GET /listings/{listing_id}
   to read the AI title and description.
   PATCH /listings/{listing_id} if you want to replace that copy — anything you
   PATCH is pinned and the AI pipeline will not overwrite it, even mid-generation.
4. Optional: POST /listings/{listing_id}/push/etsy|ebay|walmart, then poll
   GET /listings/{listing_id}/push/{marketplace}.

Rules:
- ALWAYS send a unique request_id on anything that creates or attaches. Retrying
  with the same request_id is safe and will never duplicate.
- order is 0-based and 0 is the cover image. To resequence later, POST
  /listings/{id}/media/reorder with {"media_ids":[...]} in the desired order.
- Never ignore a 409: it means the upload did not land or the bytes don't match
  the declared size/checksum. Re-upload that file.
- Story Sets: POST /story-sets then /story-sets/{id}/media/attach (media_type is
  "image"|"video"). Ad Creatives: the same under /ad-creatives.`;

const ENDPOINTS = [
  { group: 'Meta', rows: [
    ['GET', '/ping', 'Verify the key, read current limits'],
    ['GET', '/docs.md', 'Full integration guide (markdown)'],
    ['GET', '/openapi.json', 'OpenAPI 3 spec for these endpoints only'],
  ] },
  { group: 'Uploads', rows: [
    ['POST', '/uploads/presign', 'One-time PUT URL + required headers + expiry'],
    ['GET', '/uploads/{upload_id}', 'Did the PUT land? Stored size and ETag'],
  ] },
  { group: 'Listings', rows: [
    ['POST', '/listings', 'Create + run the AI pipeline'],
    ['GET', '/listings', 'Browse (search, status, limit, offset)'],
    ['GET', '/listings/{id}', 'Full listing + ordered media with fresh URLs'],
    ['GET', '/listings/{id}/wait', 'Long-poll until generation finishes'],
    ['PATCH', '/listings/{id}', 'Overwrite title / description / eBay copy'],
    ['POST', '/listings/{id}/media/attach', 'Register one upload at an exact order'],
    ['POST', '/listings/{id}/media/attach-batch', 'Register many, in order'],
    ['POST', '/listings/{id}/media/upload', 'Multipart fallback (≤10 MB)'],
    ['POST', '/listings/{id}/media/ingest', 'Fetch public URLs in order'],
    ['POST', '/listings/{id}/media/reorder', 'Set the exact sequence'],
    ['DELETE', '/listings/{id}/media/{media_id}', 'Remove one file'],
    ['DELETE', '/listings/{id}?confirm=true', 'Permanently delete the listing + media'],
  ] },
  { group: 'Story Sets', rows: [
    ['POST', '/story-sets', 'Create a set (optionally linked to a listing)'],
    ['GET', '/story-sets', 'List all sets'],
    ['GET', '/story-sets/{id}', 'One set with ordered media'],
    ['POST', '/story-sets/{id}/media/attach', 'Attach one upload'],
    ['POST', '/story-sets/{id}/media/attach-batch', 'Attach many, in order'],
    ['POST', '/story-sets/{id}/media/reorder', 'Set the play order'],
    ['DELETE', '/story-sets/{id}/media/{media_id}', 'Remove one file'],
    ['DELETE', '/story-sets/{id}?confirm=true', 'Permanently delete the set + media'],
  ] },
  { group: 'Ad Creatives', rows: [
    ['POST', '/ad-creatives', 'Create a creative set'],
    ['GET', '/ad-creatives', 'List all sets'],
    ['GET', '/ad-creatives/{id}', 'One set with ordered media'],
    ['POST', '/ad-creatives/{id}/media/attach', 'Attach one upload'],
    ['POST', '/ad-creatives/{id}/media/attach-batch', 'Attach many, in order'],
    ['POST', '/ad-creatives/{id}/media/reorder', 'Set the order'],
    ['DELETE', '/ad-creatives/{id}/media/{media_id}', 'Remove one file'],
    ['DELETE', '/ad-creatives/{id}?confirm=true', 'Permanently delete the set + media'],
  ] },
  { group: 'Marketplace pushes', rows: [
    ['POST', '/listings/{id}/push/{marketplace}', 'etsy · ebay · walmart — same pre-flight as the UI'],
    ['GET', '/listings/{id}/push/{marketplace}', 'Poll the push / feed state'],
  ] },
];

const METHOD_STYLES = {
  GET: 'bg-sky-50 text-sky-700 border-sky-200',
  POST: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  PATCH: 'bg-amber-50 text-amber-700 border-amber-200',
  DELETE: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function Developers() {
  const [key, setKey] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ping, setPing] = useState(null);
  const [pinging, setPinging] = useState(false);
  const [copied, setCopied] = useState(null);
  const [tab, setTab] = useState('curl');

  useEffect(() => {
    (async () => {
      try {
        const res = await agentApiAPI.getKey();
        setKey(res.data.api_key || '');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load the API key');
      } finally { setLoading(false); }
    })();
  }, []);

  const copy = async (text, tag) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag);
      setTimeout(() => setCopied(null), 1600);
      toast.success('Copied');
    } catch {
      toast.error('Your browser blocked the clipboard — select the text and copy manually');
    }
  };

  const runPing = async () => {
    try {
      setPinging(true);
      const res = await agentApiAPI.ping(key);
      setPing(res.data);
      toast.success('Key works — API is live');
    } catch (err) {
      setPing({ error: err.response?.data?.detail || 'Request failed' });
      toast.error('Ping failed');
    } finally { setPinging(false); }
  };

  const masked = key ? `${key.slice(0, 12)}${'•'.repeat(Math.max(0, key.length - 16))}${key.slice(-4)}` : '';
  const snippet = tab === 'curl' ? CURL_QUICKSTART : PY_QUICKSTART;

  return (
    <div className="max-w-5xl mx-auto px-5 py-8" data-testid="developers-page">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-zinc-900 text-white grid place-items-center flex-shrink-0">
          <Terminal size={18} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Agent API</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Create listings, upload media in a guaranteed order and push to marketplaces from code — no browser session, no expiring token.
          </p>
        </div>
      </div>

      {/* Key + connection */}
      <div className="mt-7 rounded-xl border border-zinc-200 bg-white p-5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-zinc-500">
          <KeyRound size={12} /> Credentials
        </div>
        <div className="mt-3 grid gap-3">
          <Row label="Base URL" value={PREFIX} onCopy={() => copy(PREFIX, 'base')} copied={copied === 'base'} testid="dev-base-url" />
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Header</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[12px] font-mono bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 truncate" data-testid="dev-api-key">
                {loading ? 'loading…' : `X-API-Key: ${revealed ? key : masked}`}
              </code>
              <Button size="sm" variant="outline" className="h-9" onClick={() => setRevealed((v) => !v)} data-testid="dev-reveal-key">
                {revealed ? <EyeOff size={13} /> : <Eye size={13} />}
              </Button>
              <Button size="sm" variant="outline" className="h-9" onClick={() => copy(`X-API-Key: ${key}`, 'key')} disabled={!key} data-testid="dev-copy-key">
                {copied === 'key' ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
              </Button>
            </div>
            <p className="text-[11px] text-zinc-400 mt-1">
              Never expires. Treat it like a password — anything it can read or write, the holder can too.
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Button onClick={runPing} disabled={pinging || !key} size="sm" className="bg-zinc-900 hover:bg-zinc-800 text-white h-8 text-xs" data-testid="dev-ping-btn">
            {pinging ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <ShieldCheck size={12} className="mr-1.5" />}
            Test the connection
          </Button>
          <a href={`${PREFIX}/docs.md?api_key=${key}`} target="_blank" rel="noreferrer" data-testid="dev-docs-link">
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!key}>
              <ExternalLink size={12} className="mr-1.5" /> Full reference
            </Button>
          </a>
          <a href={`${PREFIX}/openapi.json?api_key=${key}`} target="_blank" rel="noreferrer" data-testid="dev-openapi-link">
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!key}>
              <FileJson size={12} className="mr-1.5" /> OpenAPI spec
            </Button>
          </a>
        </div>

        {ping && (
          <pre className="mt-3 text-[11px] font-mono bg-zinc-900 text-zinc-100 rounded-lg p-3 overflow-auto max-h-60" data-testid="dev-ping-result">
            {JSON.stringify(ping, null, 2)}
          </pre>
        )}
      </div>

      {/* How uploads work */}
      <div className="mt-6 grid gap-3 md:grid-cols-3">
        <Card icon={CloudUpload} title="1 · Presign" body="POST /uploads/presign returns an upload URL, the exact headers to send, the object key and an expiry. Supply the file's base64 MD5 and storage will reject a corrupted body outright." />
        <Card icon={Rocket} title="2 · Raw PUT" body="Send the bytes straight to storage — they never pass through the app, so large files are fine. Limits and URL expiry still apply; re-presign if it lapses." />
        <Card icon={ListOrdered} title="3 · Attach" body="POST …/media/attach with the object key, media type, order and cover flag. Size and checksum are verified first, so a truncated upload is refused with 409 instead of silently landing." />
      </div>

      {/* Quickstart */}
      <div className="mt-6 rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">Quickstart — listing with ordered media</div>
          <div className="flex items-center gap-1">
            {['curl', 'python'].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`h-7 px-3 rounded-md text-[11px] font-medium transition ${tab === t ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100'}`}
                data-testid={`dev-tab-${t}`}
              >
                {t}
              </button>
            ))}
            <Button size="sm" variant="outline" className="h-7 ml-1" onClick={() => copy(snippet, 'snippet')} data-testid="dev-copy-snippet">
              {copied === 'snippet' ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
            </Button>
          </div>
        </div>
        <pre className="text-[11.5px] leading-relaxed font-mono bg-zinc-950 text-zinc-100 p-5 overflow-auto max-h-[460px]" data-testid="dev-snippet">
          {snippet}
        </pre>
      </div>

      {/* Ordering rules */}
      <div className="mt-6 rounded-xl border border-zinc-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">Ordering, cover image and retries</div>
        <ul className="mt-3 space-y-2 text-[13px] text-zinc-600">
          <li><b className="text-zinc-900">order</b> is 0-based and always kept gapless. <code className="text-[11px] bg-zinc-100 px-1 rounded">order: 0</code> is the first image everywhere — AJHQ, Etsy, eBay and the Walmart feed.</li>
          <li><b className="text-zinc-900">cover: true</b> forces position 0 and pushes the rest down. Inserting mid-sequence renumbers the others automatically.</li>
          <li><b className="text-zinc-900">/media/reorder</b> takes <code className="text-[11px] bg-zinc-100 px-1 rounded">{'{"media_ids":[…]}'}</code> and sets the whole sequence in one call; anything you leave out keeps its relative order at the end.</li>
          <li><b className="text-zinc-900">request_id</b> on any create or attach makes it replay-safe: a repeat returns the first response with <code className="text-[11px] bg-zinc-100 px-1 rounded">idempotent_replay: true</code> and never duplicates.</li>
          <li><b className="text-zinc-900">409</b> means the upload did not land, or the stored bytes don&apos;t match the declared size/checksum. Re-upload that file — never force past it.</li>
          <li>Bytes are never re-encoded. The staged upload is copied server-side to its final SEO-named key, so what you send is exactly what the marketplaces get.</li>
        </ul>
      </div>

      {/* Endpoints */}
      <div className="mt-6 rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid="dev-endpoints">
        <div className="px-5 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-wider font-semibold text-zinc-500">
          Endpoints
        </div>
        {ENDPOINTS.map((g) => (
          <div key={g.group}>
            <div className="px-5 py-2 bg-zinc-50 text-[11px] font-semibold text-zinc-600 border-y border-zinc-100">{g.group}</div>
            {g.rows.map(([method, path, desc]) => (
              <div key={`${method}-${path}`} className="px-5 py-2.5 flex items-start gap-3 border-b border-zinc-50 last:border-0 hover:bg-zinc-50/60">
                <span className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded border flex-shrink-0 w-16 text-center ${METHOD_STYLES[method]}`}>{method}</span>
                <code className="text-[12px] font-mono text-zinc-800 flex-shrink-0">{path}</code>
                <span className="text-[12px] text-zinc-500">{desc}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Agent prompt */}
      <div className="mt-6 rounded-xl border border-violet-200 bg-violet-50/40 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-violet-100">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-700">Paste this into your agent</div>
            <div className="text-[11px] text-violet-600/80">Everything it needs — endpoints, order rules, retry rules. Swap in the key above.</div>
          </div>
          <Button size="sm" variant="outline" className="h-8 border-violet-300 text-violet-700" onClick={() => copy(AGENT_PROMPT.replace('<your-api-key>', key), 'prompt')} data-testid="dev-copy-prompt">
            {copied === 'prompt' ? <Check size={12} className="mr-1.5 text-emerald-600" /> : <Copy size={12} className="mr-1.5" />}
            Copy with key
          </Button>
        </div>
        <pre className="text-[11.5px] leading-relaxed font-mono bg-white text-zinc-700 p-5 overflow-auto max-h-[420px] whitespace-pre-wrap" data-testid="dev-agent-prompt">
          {AGENT_PROMPT}
        </pre>
      </div>

      <p className="mt-6 text-[11px] text-zinc-400">
        No webhooks yet — <code className="bg-zinc-100 px-1 rounded">GET /listings/{'{id}'}/wait?timeout=45</code> long-polls so your agent doesn&apos;t spin (loop it while <code className="bg-zinc-100 px-1 rounded">poll_again</code> is true — the edge closes any request at 60s), and the push-status endpoint covers the async marketplace feeds. Ask if you want signed callbacks instead.
      </p>
    </div>
  );
}

function Row({ label, value, onCopy, copied, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-[12px] font-mono bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 truncate" data-testid={testid}>{value}</code>
        <Button size="sm" variant="outline" className="h-9" onClick={onCopy}>
          {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
        </Button>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="flex items-center gap-2 text-zinc-900">
        <Icon size={14} />
        <div className="text-[13px] font-semibold">{title}</div>
      </div>
      <p className="text-[12px] text-zinc-500 mt-1.5 leading-relaxed">{body}</p>
    </div>
  );
}
